import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Modal, OverlayTrigger, Popover, Spinner, Tooltip } from "react-bootstrap";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Code,
  Eye,
  EyeOff,
  ExternalLink,
  Info,
  KeyRound,
  ListChecks,
  Lock,
  Pencil,
  Play,
  Plus,
  Power,
  RefreshCw,
  Save,
  ShieldAlert,
  ShieldCheck,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import {
  JobScheduleRow,
  listJobSchedules,
  reloadJobSchedules,
  triggerJobSchedule,
  updateJobSchedule,
  fetchSmsDeliverySeed,
} from "../../_services/JobScheduleService";
import PageGuard from "../../access-control/PageGuard";
import { useAccessControl } from "../../access-control/AccessControlContext";
import PageAccessSettingsButton from "../../access-control/PageAccessSettingsButton";
import CronEditor from "./CronEditor";
import "./job-schedules.css";

const PAGE_KEY = "job-schedules";

type TabKey = "all" | "enabled" | "failed" | "missing" | "notInDb";

/* ── Helpers ── */

const errorMessage = (err: any, fallback: string): string => {
  const e = err?.response?.data?.error ?? err?.response?.data ?? err;
  if (typeof e === "string") return e;
  if (e && typeof e === "object") {
    if (typeof e.message === "string") return e.message;
    try {
      return JSON.stringify(e);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const isAuthError = (msg: string): boolean => {
  const m = (msg || "").toLowerCase();
  return (
    m.includes("authorization header") ||
    m.includes("authentication") ||
    m.includes("token")
  );
};

/** Plain-English explanation of a 5-field cron expression. */
const describeCron = (expr: string): string => {
  if (!expr) return "";
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return "Custom expression";
  const [m, h, dom, mon, dow] = parts;
  if (m === "0" && h === "*" && dom === "*" && mon === "*" && dow === "*") {
    return "Every hour at :00";
  }
  if (m === "*" && h === "*" && dom === "*" && mon === "*" && dow === "*") {
    return "Every minute";
  }
  if (m.startsWith("*/") && h === "*" && dom === "*" && mon === "*" && dow === "*") {
    return `Every ${m.slice(2)} minutes`;
  }
  if (h === "*" && dom === "*" && mon === "*" && dow === "*") {
    return `Hourly at :${m.padStart(2, "0")}`;
  }
  if (dom === "*" && mon === "*" && dow === "*") {
    return `Daily at ${h.padStart(2, "0")}:${m.padStart(2, "0")} UTC`;
  }
  if (dom === "*" && mon === "*") {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const d = days[Number(dow)] ?? `dow=${dow}`;
    return `Weekly on ${d} at ${h.padStart(2, "0")}:${m.padStart(2, "0")} UTC`;
  }
  return "Custom expression";
};

const formatRunAt = (iso: string | null): string => {
  if (!iso) return "—";
  return iso.replace("T", " ").replace("Z", "");
};

const TAB_META: Record<TabKey, { title: string; subtitle: string; cardTitle: string; cardDesc: string }> = {
  all: {
    title: "Job Schedules",
    subtitle:
      "Configure cron jobs that run inside nodefork. Enable / disable a job, edit its cron expression, or trigger it manually.",
    cardTitle: "All jobs",
    cardDesc:
      "Every scheduled job registered against this tenant. The scheduler refreshes from the database on Reload or on save.",
  },
  enabled: {
    title: "Enabled jobs",
    subtitle: "Jobs currently armed to fire on their cron tick.",
    cardTitle: "Enabled jobs",
    cardDesc: "These jobs will execute on their next scheduled tick. Toggle off to skip a job without deleting it.",
  },
  failed: {
    title: "Last run failed",
    subtitle: "Jobs whose most recent execution ended in FAILED status. Investigate before re-triggering.",
    cardTitle: "Failed jobs",
    cardDesc: "The runner logged an error on the last invocation. Check server logs for the stack trace.",
  },
  missing: {
    title: "Missing runner",
    subtitle: "Schedules in the DB with no corresponding runner code registered in nodefork — they will be silently skipped.",
    cardTitle: "Missing runner",
    cardDesc: "These rows exist in job_schedule but no runner is registered for their JobKey. Either delete the row or implement the runner.",
  },
  notInDb: {
    title: "Not in DB",
    subtitle: "Jobs registered in nodefork code but with no row in this tenant's job_schedule table. Click Create to bootstrap them with their default cron.",
    cardTitle: "Registered but not in DB",
    cardDesc: "These JobKeys exist in JOB_METADATA but no row has been inserted into job_schedule for this tenant. Until you create the row the scheduler will not pick them up.",
  },
};

/* ── Page ── */

const JobSchedulesPage: React.FC = () => {
  const history = useHistory();
  const { canSeeElement } = useAccessControl();
  const canReload = canSeeElement(PAGE_KEY, "action.reloadSchedules");
  const canToggle = canSeeElement(PAGE_KEY, "action.toggleEnabled");
  const canEdit = canSeeElement(PAGE_KEY, "action.editSchedule");
  const canTrigger = canSeeElement(PAGE_KEY, "action.triggerJob");

  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [rows, setRows] = useState<JobScheduleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [triggeringKey, setTriggeringKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<JobScheduleRow | null>(null);
  const [reloading, setReloading] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listJobSchedules();
      setRows(list);
      setLastLoadedAt(new Date());
    } catch (err: any) {
      setError(errorMessage(err, "Failed to load schedules"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!info) return;
    const t = setTimeout(() => setInfo(null), 3500);
    return () => clearTimeout(t);
  }, [info]);

  const handleToggle = async (row: JobScheduleRow) => {
    setSavingKey(row.jobKey);
    setError(null);
    try {
      await updateJobSchedule(row.jobKey, { isEnabled: !row.isEnabled });
      await reloadJobSchedules();
      setInfo(
        row.isEnabled
          ? `${row.jobName} disabled. Will not fire on the next tick.`
          : `${row.jobName} enabled. Will fire on its next scheduled tick.`,
      );
      await load();
    } catch (err: any) {
      setError(errorMessage(err, "Failed to update job"));
    } finally {
      setSavingKey(null);
    }
  };

  const handleTrigger = async (row: JobScheduleRow) => {
    setTriggeringKey(row.jobKey);
    setError(null);
    try {
      await triggerJobSchedule(row.jobKey);
      setInfo(`${row.jobName} triggered manually.`);
      await load();
    } catch (err: any) {
      setError(errorMessage(err, "Failed to trigger job"));
    } finally {
      setTriggeringKey(null);
    }
  };

  const handleCreate = async (row: JobScheduleRow) => {
    setSavingKey(row.jobKey);
    setError(null);
    try {
      // PATCH with an empty patch: the upsert path on the backend creates the
      // row using meta.defaultCron and isEnabled=0. Admin can then enable / edit.
      await updateJobSchedule(row.jobKey, { isEnabled: false });
      await reloadJobSchedules();
      setInfo(`${row.metadata?.jobName || row.jobKey} created in job_schedule. Enable it when you're ready.`);
      await load();
    } catch (err: any) {
      setError(errorMessage(err, "Failed to create job schedule"));
    } finally {
      setSavingKey(null);
    }
  };

  const handleReload = async () => {
    setReloading(true);
    setError(null);
    try {
      await reloadJobSchedules();
      setInfo("Schedules reloaded from the database.");
      await load();
    } catch (err: any) {
      setError(errorMessage(err, "Failed to reload schedules"));
    } finally {
      setReloading(false);
    }
  };

  const counts = useMemo(() => {
    const inDb = rows.filter((r) => !r.notInDb);
    const enabled = inDb.filter((r) => r.isEnabled).length;
    const failed = inDb.filter((r) => r.lastStatus === "FAILED").length;
    const missing = inDb.filter((r) => !r.hasRunner).length;
    const notInDb = rows.filter((r) => r.notInDb).length;
    return { all: inDb.length, enabled, failed, missing, notInDb };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const base = (() => {
      switch (activeTab) {
        case "enabled":
          return rows.filter((r) => !r.notInDb && r.isEnabled);
        case "failed":
          return rows.filter((r) => !r.notInDb && r.lastStatus === "FAILED");
        case "missing":
          return rows.filter((r) => !r.notInDb && !r.hasRunner);
        case "notInDb":
          return rows.filter((r) => r.notInDb);
        default:
          return rows.filter((r) => !r.notInDb);
      }
    })();
    // Sort alphabetically by jobKey so related jobs (e.g. integration-*)
    // group together regardless of insertion order in the DB.
    return [...base].sort((a, b) => a.jobKey.localeCompare(b.jobKey));
  }, [rows, activeTab]);

  const showAuthCard = error ? isAuthError(error) : false;
  const meta = TAB_META[activeTab];

  return (
    <div className="job-schedules">
      <aside className="js-side">
        <h2>Job Schedules</h2>
        <NavButton
          icon={<ListChecks size={16} />}
          label="All jobs"
          count={counts.all}
          active={activeTab === "all"}
          onClick={() => setActiveTab("all")}
        />
        <NavButton
          icon={<Power size={16} />}
          label="Enabled"
          count={counts.enabled}
          active={activeTab === "enabled"}
          onClick={() => setActiveTab("enabled")}
        />
        <NavButton
          icon={<XCircle size={16} />}
          label="Last run failed"
          count={counts.failed}
          countTone={counts.failed > 0 ? "danger" : undefined}
          active={activeTab === "failed"}
          onClick={() => setActiveTab("failed")}
        />
        <NavButton
          icon={<AlertTriangle size={16} />}
          label="Missing runner"
          count={counts.missing}
          countTone={counts.missing > 0 ? "warn" : undefined}
          active={activeTab === "missing"}
          onClick={() => setActiveTab("missing")}
        />
        <NavButton
          icon={<Plus size={16} />}
          label="Not in DB"
          count={counts.notInDb}
          countTone={counts.notInDb > 0 ? "warn" : undefined}
          active={activeTab === "notInDb"}
          onClick={() => setActiveTab("notInDb")}
        />

        <div className="js-side-divider" />
        <button
          type="button"
          className="js-nav-btn js-side-back"
          onClick={() => history.push("/tenant-preferences")}
          title="Back to tenant preferences hub"
        >
          <ShieldCheck size={14} className="js-nav-icon" />
          <span className="js-nav-label">Tenant Preferences</span>
        </button>
      </aside>

      <main className="js-main">
        <div className="js-header">
          <div>
            <h1>{meta.title}</h1>
            <p className="js-subtitle">{meta.subtitle}</p>
          </div>
          <div className="js-page-access">
            <PageAccessSettingsButton pageKey={PAGE_KEY} />
          </div>
        </div>

        {error && !showAuthCard && (
          <div className="js-alert js-alert-error">
            <AlertCircle size={16} className="js-alert-icon" />
            <div className="js-alert-body">{error}</div>
            <button type="button" className="js-alert-close" onClick={() => setError(null)}>
              &times;
            </button>
          </div>
        )}
        {info && (
          <div className="js-alert js-alert-success">
            <CheckCircle2 size={16} className="js-alert-icon" />
            <div className="js-alert-body">{info}</div>
            <button type="button" className="js-alert-close" onClick={() => setInfo(null)}>
              &times;
            </button>
          </div>
        )}
        {showAuthCard && error && <AuthErrorCard message={error} />}

        <div className="js-card is-flush">
          <div className="js-card-head">
            <span className="js-card-icon">
              <Clock size={18} />
            </span>
            <h3>{meta.cardTitle}</h3>
          </div>
          <p className="js-card-desc">{meta.cardDesc}</p>

          <div className="js-table-head">
            <div>Job</div>
            <div>Schedule</div>
            <div>Last status</div>
            <div>Enabled</div>
            <div>Last run</div>
            <div>Actions</div>
          </div>

          {loading && (
            <div className="js-loading">
              <div className="js-loading-spinner" />
              Loading schedules…
            </div>
          )}

          {!loading && filteredRows.length === 0 && !error && (
            <div className="js-table-empty">
              {activeTab === "all"
                ? "No jobs configured."
                : "No jobs match this filter."}
            </div>
          )}

          {!loading &&
            filteredRows.map((row) => (
              <JobRow
                key={row.jobKey}
                row={row}
                busy={savingKey === row.jobKey || triggeringKey === row.jobKey}
                canToggle={canToggle}
                canEdit={canEdit}
                canTrigger={canTrigger}
                onToggle={() => handleToggle(row)}
                onTrigger={() => handleTrigger(row)}
                onEdit={() => setEditing(row)}
                onCreate={() => handleCreate(row)}
              />
            ))}
        </div>

        <div className="js-toolbar">
          <span className="js-toolbar-meta">
            <Clock size={13} />
            {lastLoadedAt
              ? `Loaded ${lastLoadedAt.toLocaleTimeString()}`
              : "Not loaded yet"}
            <RefreshVsReloadHelp canReload={canReload} />
          </span>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="js-tip-refresh">
                Re-reads the schedule rows from the database and updates this
                table. Doesn't affect the running scheduler.
              </Tooltip>
            }
          >
            <button
              type="button"
              className="js-btn js-btn-secondary"
              onClick={load}
              disabled={loading}
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </OverlayTrigger>
          {canReload && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="js-tip-reload">
                  Tells nodefork to drop its in-memory cron map and re-register
                  every job from <code>job_schedule</code>. Use this after
                  changing schedules outside this UI.
                </Tooltip>
              }
            >
              <button
                type="button"
                className="js-btn js-btn-primary"
                onClick={handleReload}
                disabled={reloading || loading}
              >
                <Zap size={14} /> {reloading ? "Reloading…" : "Reload scheduler"}
              </button>
            </OverlayTrigger>
          )}
        </div>
      </main>

      {editing && (
        <EditDrawer
          row={editing}
          allRows={rows}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => {
            setEditing(null);
            setInfo(msg);
            await load();
          }}
          onError={(msg) => setError(msg)}
        />
      )}
    </div>
  );
};

/* ── Sub-components ── */

const RefreshVsReloadHelp: React.FC<{ canReload: boolean }> = ({ canReload }) => {
  const popover = (
    <Popover id="js-help-refresh-vs-reload" style={{ maxWidth: 360 }}>
      <Popover.Header
        as="h3"
        style={{
          fontSize: 13,
          fontWeight: 600,
          padding: "8px 12px",
          background: "var(--brand-primary-subtle)",
          color: "var(--brand-primary-active)",
        }}
      >
        Refresh vs Reload scheduler
      </Popover.Header>
      <Popover.Body style={{ padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, color: "var(--text-body)" }}>
        <div style={{ marginBottom: 8 }}>
          <strong style={{ color: "var(--text-strong)" }}>Refresh</strong> — re-reads{" "}
          <code>job_schedule</code> from the database so the table shows the
          latest rows. Safe to click anytime; no side effects on running jobs.
        </div>
        {canReload && (
          <div>
            <strong style={{ color: "var(--text-strong)" }}>Reload scheduler</strong> —
            tells the running nodefork process to drop its in-memory cron map
            and re-register every job from the DB. Needed when schedules were
            changed outside this UI (direct SQL, deploy script, etc.).
          </div>
        )}
        {canReload && (
          <div style={{ marginTop: 8, padding: "6px 8px", background: "var(--surface-sunken)", borderRadius: 6, fontSize: 11.5, color: "var(--text-body)" }}>
            Edits and toggles done in this UI auto-call <em>Reload scheduler</em>
            for you — you only need the bare button after out-of-band changes.
          </div>
        )}
      </Popover.Body>
    </Popover>
  );
  return (
    <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={popover}>
      <span
        role="button"
        tabIndex={0}
        style={{
          cursor: "help",
          marginLeft: 8,
          opacity: 0.65,
          display: "inline-flex",
          verticalAlign: "middle",
          color: "var(--brand-primary)",
        }}
      >
        <Info size={14} />
      </span>
    </OverlayTrigger>
  );
};

const NavButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  active: boolean;
  countTone?: "warn" | "danger";
  onClick: () => void;
}> = ({ icon, label, count, active, countTone, onClick }) => (
  <button
    type="button"
    className={`js-nav-btn${active ? " active" : ""}`}
    onClick={onClick}
  >
    <span className="js-nav-icon">{icon}</span>
    <span className="js-nav-label">{label}</span>
    <span className={`js-nav-count${countTone ? " " + countTone : ""}`}>{count}</span>
  </button>
);

const JobRow: React.FC<{
  row: JobScheduleRow;
  busy: boolean;
  canToggle: boolean;
  canEdit: boolean;
  canTrigger: boolean;
  onToggle: () => void;
  onTrigger: () => void;
  onEdit: () => void;
  onCreate: () => void;
}> = ({ row, busy, canToggle, canEdit, canTrigger, onToggle, onTrigger, onEdit, onCreate }) => {
  // Fallback chain: DB JobName → metadata jobName → jobKey. Guards against
  // rows seeded with an empty JobName so the card always has a title.
  const displayName = row.jobName?.trim() || row.metadata?.jobName || row.jobKey;
  const isEnrollmentScan = row.jobKey === "integration-enrollment-scan";
  const isIntegrationRow = row.jobKey.startsWith("integration-");
  const history = useHistory();
  const [showTriggerPoints, setShowTriggerPoints] = useState(false);
  return (
  <div className={`js-row${row.isEnabled ? "" : " is-disabled"}`}>
    <div className="js-cell-min">
      <div className="js-job-name">
        <span className="js-job-title" title={displayName}>
          {displayName}
        </span>
        {row.notInDb && (
          <span
            title="Registered in nodefork code (JOB_METADATA) but no row exists in job_schedule for this tenant. Click Create to bootstrap it with its default cron."
            className="js-tag js-tag-warn"
          >
            <Plus size={10} /> NOT IN DB
          </span>
        )}
        {!row.hasRunner && !row.notInDb && (
          <span
            title="No runner code is registered for this JobKey. The schedule will be skipped."
            className="js-tag js-tag-warn"
          >
            <AlertTriangle size={10} /> NO RUNNER
          </span>
        )}
        {row.metadata?.configurable && (
          <span
            title="Per-job Config JSON is meaningful for this job"
            className="js-tag js-tag-info"
          >
            <Code size={10} /> CONFIG
          </span>
        )}
        {isEnrollmentScan && (
          <button
            type="button"
            title="See UC1 – UC7: what triggers each integration job and when"
            className="js-tag js-tag-triggers"
            onClick={(e) => {
              e.stopPropagation();
              setShowTriggerPoints(true);
            }}
          >
            <Info size={10} /> TRIGGERS
          </button>
        )}
        {isIntegrationRow && (
          <button
            type="button"
            title="Open Integration Monitor — browse integration_job and integration_user_mapping rows"
            className="js-tag js-tag-monitor"
            onClick={(e) => {
              e.stopPropagation();
              history.push("/integration-monitor");
            }}
          >
            <Activity size={10} /> MONITOR
          </button>
        )}
      </div>
      <div className="js-job-key">{row.jobKey}</div>
      {row.metadata?.description && (
        <div className="js-job-desc">{row.metadata.description}</div>
      )}
      {row.metadata?.warning && (
        <div className="js-warn-inline">
          <AlertTriangle size={11} /> {row.metadata.warning}
        </div>
      )}
    </div>

    <div className="js-cell-min">
      <div className="js-cron-expr">
        {row.cronExprUTC || (row.notInDb ? "(no default cron)" : "")}
      </div>
      <div className="js-cron-desc">
        {row.cronExprUTC ? describeCron(row.cronExprUTC) : ""}
      </div>
    </div>

    <div>
      {row.notInDb ? (
        <span className="js-pill js-pill-neutral">Not configured</span>
      ) : (
        <>
          {row.lastStatus === "SUCCESS" && (
            <span className="js-pill js-pill-success">
              <CheckCircle2 size={11} /> Success
            </span>
          )}
          {row.lastStatus === "FAILED" && (
            <span className="js-pill js-pill-failed">
              <XCircle size={11} /> Failed
            </span>
          )}
          {!row.lastStatus && <span className="js-pill js-pill-neutral">Never run</span>}
        </>
      )}
    </div>

    <div>
      <ToggleSwitch
        id={`enable-${row.jobKey}`}
        checked={!!row.isEnabled}
        disabled={busy || !canToggle || row.notInDb}
        onChange={onToggle}
      />
    </div>

    <div className="js-runat">{formatRunAt(row.lastRunAt)}</div>

    <div className="js-actions">
      {row.notInDb ? (
        canEdit && (
          <button
            type="button"
            onClick={onCreate}
            disabled={busy || !row.cronExprUTC}
            title={
              row.cronExprUTC
                ? "Create the row in job_schedule using the default cron. Disabled by default — enable it afterwards."
                : "This job has no defaultCron in metadata. Add one in JOB_METADATA before bootstrapping."
            }
            className="js-btn-edit"
          >
            {busy ? <Spinner animation="border" size="sm" /> : <Plus size={12} />}
            Create
          </button>
        )
      ) : (
        <>
          {canEdit && (
            <button type="button" onClick={onEdit} title="Edit cron / config" className="js-btn-edit">
              <Pencil size={12} /> Edit
            </button>
          )}
          {canTrigger && (
            <button
              type="button"
              onClick={onTrigger}
              disabled={busy || !row.hasRunner}
              title={
                row.hasRunner
                  ? "Run this job immediately (regardless of enabled state)"
                  : "No runner registered — can't trigger"
              }
              className="js-btn-run"
            >
              {busy ? <Spinner animation="border" size="sm" /> : <Play size={12} />}
              Run now
            </button>
          )}
        </>
      )}
    </div>
    {isEnrollmentScan && (
      <TriggerPointsModal
        show={showTriggerPoints}
        onHide={() => setShowTriggerPoints(false)}
      />
    )}
  </div>
  );
};

const ToggleSwitch: React.FC<{
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}> = ({ id, checked, disabled, onChange }) => (
  <label htmlFor={id} className={`js-switch${disabled ? " is-disabled" : ""}`}>
    <input
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={() => !disabled && onChange()}
    />
    <span className="js-slider" />
  </label>
);

/* ── integration-processor structured form ── */

type TargetSystemKey = "M365" | "BLACKBOARD" | "SMARTGANTRY";

interface BlackboardCreds {
  applicationKey?: string;
  secret?: string;
  applicationId?: string;
  baseUrl?: string;
}
interface M365Creds {
  baseUrl?: string;
  authToken?: string;
  domainName?: string;
}
interface M365PasswordPolicy {
  length?: number;
  includeUpper?: boolean;
  includeLower?: boolean;
  includeDigit?: boolean;
  includeSpecial?: boolean;
  specialChars?: string;
}
interface ProcessorConfig {
  activeSystems?: TargetSystemKey[];
  credentials?: {
    M365?: M365Creds;
    BLACKBOARD?: BlackboardCreds;
    SMARTGANTRY?: Record<string, unknown>;
  };
  passwordPolicy?: {
    M365?: M365PasswordPolicy;
  };
}

// Backend soft-cap — nodefork resolveM365PasswordPolicy clamps to this so
// the generated password never exceeds the storage column width. Kept in
// sync manually with INTEGRATION_USER_PASSWORD_LENGTH in nodefork/types.ts.
const M365_PASSWORD_MAX_LENGTH = 12;
const M365_PASSWORD_MIN_LENGTH = 4;

/**
 * Strip empty-string / null / undefined fields recursively. Lets us send a
 * clean Config to the backend: any field the admin leaves blank simply drops
 * out of the JSON, so getAdapter()'s env-var fallback kicks in instead of the
 * blank value masking it.
 */
function pruneEmpty(obj: any): any {
  if (obj == null) return undefined;
  if (Array.isArray(obj)) return obj;
  if (typeof obj !== "object") return obj;
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v == null) continue;
    if (typeof v === "string") {
      if (v.trim() === "") continue;
      out[k] = v;
    } else if (typeof v === "object" && !Array.isArray(v)) {
      const sub = pruneEmpty(v);
      if (sub && Object.keys(sub).length > 0) out[k] = sub;
    } else {
      out[k] = v;
    }
  }
  return out;
}

const PlainField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <div className="ip-field">
    <label>{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="ip-input"
      autoComplete="off"
      spellCheck={false}
    />
  </div>
);

const SecretField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="ip-field">
      <label>{label}</label>
      <div className="ip-input-with-toggle">
        <input
          type={reveal ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="ip-input"
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          className="ip-reveal-btn"
          onClick={() => setReveal((r) => !r)}
          title={reveal ? "Hide" : "Reveal"}
          aria-label={reveal ? "Hide value" : "Reveal value"}
        >
          {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
};

const IntegrationProcessorForm: React.FC<{
  initial: ProcessorConfig;
  onChange: (cfg: ProcessorConfig) => void;
}> = ({ initial, onChange }) => {
  const [cfg, setCfg] = useState<ProcessorConfig>(initial);

  const update = (next: ProcessorConfig) => {
    setCfg(next);
    onChange(next);
  };

  const active = cfg.activeSystems ?? [];
  const bb = cfg.credentials?.BLACKBOARD ?? {};
  const m365 = cfg.credentials?.M365 ?? {};

  const toggle = (sys: TargetSystemKey) => {
    const next = active.includes(sys)
      ? active.filter((s) => s !== sys)
      : [...active, sys];
    update({ ...cfg, activeSystems: next });
  };

  const setBb = (field: keyof BlackboardCreds, value: string) => {
    update({
      ...cfg,
      credentials: {
        ...cfg.credentials,
        BLACKBOARD: { ...bb, [field]: value },
      },
    });
  };

  const setM365 = (field: keyof M365Creds, value: string) => {
    update({
      ...cfg,
      credentials: {
        ...cfg.credentials,
        M365: { ...m365, [field]: value },
      },
    });
  };

  const m365Policy = cfg.passwordPolicy?.M365 ?? {};
  const setM365Policy = <K extends keyof M365PasswordPolicy>(
    field: K,
    value: M365PasswordPolicy[K],
  ) => {
    update({
      ...cfg,
      passwordPolicy: {
        ...cfg.passwordPolicy,
        M365: { ...m365Policy, [field]: value },
      },
    });
  };

  return (
    <div className="ip-form">
      <section className="ip-section">
        <h4 className="ip-section-title">
          <ShieldCheck size={14} /> Active systems
        </h4>
        <div className="ip-help">
          Disabled systems are SKIPPED at the processor and aren&apos;t enqueued by the orchestrator.
        </div>
        <label className="ip-checkbox">
          <input
            type="checkbox"
            checked={active.includes("BLACKBOARD")}
            onChange={() => toggle("BLACKBOARD")}
          />
          Blackboard
        </label>
        <label className="ip-checkbox">
          <input
            type="checkbox"
            checked={active.includes("M365")}
            onChange={() => toggle("M365")}
          />
          Microsoft 365 <span className="ip-sub">(via ADManager Plus)</span>
        </label>
        <label className="ip-checkbox">
          <input
            type="checkbox"
            checked={active.includes("SMARTGANTRY")}
            onChange={() => toggle("SMARTGANTRY")}
          />
          SmartGantry <span className="ip-sub">(no credentials needed)</span>
        </label>
      </section>

      <section className="ip-section">
        <h4 className="ip-section-title">
          <KeyRound size={14} /> Blackboard credentials
        </h4>
        <div className="ip-help">
          Leave a field blank to fall back to APP_BLACKBOARD__* env vars.
        </div>
        <PlainField
          label="Application Key"
          value={bb.applicationKey ?? ""}
          onChange={(v) => setBb("applicationKey", v)}
        />
        <SecretField
          label="Secret"
          value={bb.secret ?? ""}
          onChange={(v) => setBb("secret", v)}
        />
        <PlainField
          label="Application ID"
          value={bb.applicationId ?? ""}
          onChange={(v) => setBb("applicationId", v)}
        />
        <PlainField
          label="Base URL"
          value={bb.baseUrl ?? ""}
          onChange={(v) => setBb("baseUrl", v)}
          placeholder="https://your-tenant.blackboard.com"
        />
      </section>

      <section className="ip-section">
        <h4 className="ip-section-title">
          <KeyRound size={14} /> Microsoft 365 (ADManager Plus)
        </h4>
        <div className="ip-help">
          Leave a field blank to fall back to APP_ADMANAGERPLUS__* env vars.
        </div>
        <PlainField
          label="Base URL"
          value={m365.baseUrl ?? ""}
          onChange={(v) => setM365("baseUrl", v)}
          placeholder="https://admp.example.com:1008"
        />
        <SecretField
          label="Auth Token"
          value={m365.authToken ?? ""}
          onChange={(v) => setM365("authToken", v)}
        />
        <PlainField
          label="AD Domain Name"
          value={m365.domainName ?? ""}
          onChange={(v) => setM365("domainName", v)}
          placeholder="company.edu"
        />
      </section>

      <section className="ip-section">
        <h4 className="ip-section-title">
          <Lock size={14} /> M365 password policy
        </h4>
        <div className="ip-help">
          Applies to every student account created via ADManager Plus. Leave
          fields at their default to use the built-in AD policy
          ({M365_PASSWORD_MAX_LENGTH} chars, all four categories, specials{" "}
          <code>!@#$*</code>). Length is capped at{" "}
          <strong>{M365_PASSWORD_MAX_LENGTH}</strong> chars — the storage
          column width; widen the column first if you need more.
        </div>

        <div className="ip-field">
          <label>Password length</label>
          <input
            type="number"
            min={M365_PASSWORD_MIN_LENGTH}
            max={M365_PASSWORD_MAX_LENGTH}
            value={m365Policy.length ?? M365_PASSWORD_MAX_LENGTH}
            onChange={(e) => {
              const raw = Number(e.target.value);
              if (!Number.isFinite(raw)) return;
              const clamped = Math.max(
                M365_PASSWORD_MIN_LENGTH,
                Math.min(M365_PASSWORD_MAX_LENGTH, Math.floor(raw)),
              );
              setM365Policy("length", clamped);
            }}
            className="ip-input"
          />
        </div>

        <label className="ip-checkbox">
          <input
            type="checkbox"
            checked={m365Policy.includeUpper ?? true}
            onChange={(e) => setM365Policy("includeUpper", e.target.checked)}
          />
          Require uppercase <span className="ip-sub">(A–Z)</span>
        </label>
        <label className="ip-checkbox">
          <input
            type="checkbox"
            checked={m365Policy.includeLower ?? true}
            onChange={(e) => setM365Policy("includeLower", e.target.checked)}
          />
          Require lowercase <span className="ip-sub">(a–z)</span>
        </label>
        <label className="ip-checkbox">
          <input
            type="checkbox"
            checked={m365Policy.includeDigit ?? true}
            onChange={(e) => setM365Policy("includeDigit", e.target.checked)}
          />
          Require digit <span className="ip-sub">(0–9)</span>
        </label>
        <label className="ip-checkbox">
          <input
            type="checkbox"
            checked={m365Policy.includeSpecial ?? true}
            onChange={(e) => setM365Policy("includeSpecial", e.target.checked)}
          />
          Require special character
        </label>

        <PlainField
          label="Allowed special characters"
          value={m365Policy.specialChars ?? ""}
          onChange={(v) => setM365Policy("specialChars", v)}
          placeholder="!@#$*"
        />
      </section>
    </div>
  );
};

/* ── Config preview modal (click CONFIG tag → see current JSON) ── */

/** Field names whose values are secrets and should be masked in the preview. */
const MASKED_FIELDS = new Set([
  "authToken",
  "secret",
  "applicationKey",
  "applicationId",
  "password",
  "apiKey",
  "accessToken",
  "refreshToken",
  "clientSecret",
  "privateKey",
]);

/** Replace secret values with a masked sentinel that preserves length-class. */
function maskSecrets(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(maskSecrets);
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (MASKED_FIELDS.has(k) && typeof v === "string" && v.length > 0) {
        // Preserve the last 4 chars so admins can spot-check which token is loaded
        const tail = v.length > 4 ? v.slice(-4) : v;
        out[k] = `***${tail}`;
      } else {
        out[k] = maskSecrets(v);
      }
    }
    return out;
  }
  return value;
}

const ConfigPreviewModal: React.FC<{
  show: boolean;
  onHide: () => void;
  jobName: string;
  jobKey: string;
  config: unknown;
  onEdit?: () => void;
}> = ({ show, onHide, jobName, jobKey, config, onEdit }) => {
  const masked = useMemo(() => maskSecrets(config), [config]);
  const isEmpty =
    config === null ||
    config === undefined ||
    (typeof config === "object" &&
      !Array.isArray(config) &&
      Object.keys(config as Record<string, unknown>).length === 0);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="js-cp-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <Code size={16} style={{ verticalAlign: "middle", marginRight: 8 }} />
          Config — {jobName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="js-cp-keyline">
          <code>{jobKey}</code>
        </div>
        {isEmpty ? (
          <div className="js-cp-empty">
            No Config set. Defaults from the runner are used.
          </div>
        ) : (
          <pre className="js-cp-json">
            {JSON.stringify(masked, null, 2)}
          </pre>
        )}
        <div className="js-cp-note">
          Secret-looking fields (auth tokens, secrets, etc.) are masked here for
          read-only viewing. Open <strong>Edit</strong> to modify.
        </div>
      </Modal.Body>
      <Modal.Footer>
        {onEdit && (
          <button
            type="button"
            className="js-btn js-btn-primary"
            onClick={() => {
              onHide();
              onEdit();
            }}
          >
            <Pencil size={14} /> Edit
          </button>
        )}
        <button type="button" className="js-btn js-btn-secondary" onClick={onHide}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

/* ── Trigger points modal (integration UCs) ── */

type UCJobType = "PROVISION" | "DEPROVISION" | "UPDATE_ID" | "REVOKE_LICENSE";

interface UCEntry {
  id: string;
  name: string;
  trigger: "scheduled" | "inline";
  fires: string;
  /** One or more job types this UC enqueues. UC5 fires both DEPROVISION (BB/SG) and REVOKE_LICENSE (M365). */
  jobTypes: UCJobType[];
  scope: string;
  details: string;
  /**
   * If this UC honors the cron's cutoffDate / lookbackDays (via
   * computeAnchorFloor), this describes which enrollment date column it
   * filters against. Null for UCs that ignore the floor entirely.
   */
  anchorFloor: { column: string; note: string } | null;
}

const UC_BREAKDOWN: UCEntry[] = [
  {
    id: "UC1",
    name: "New-enrollment provision (also cron backstop for UC4)",
    trigger: "scheduled",
    fires: "Every cron tick",
    jobTypes: ["PROVISION"],
    scope: "All active systems",
    details:
      "Enqueues a PROVISION job for every (Enrolled, course-start ≤ courseStartWithinDays from now, has OpenId) student who doesn't have an open PROVISION cycle yet. Honors cutoffDate / lookbackDays on student_enrollment.CreationDate. The course-start window is configurable via Config.courseStartWithinDays (defaults to 7).",
    anchorFloor: {
      column: "student_enrollment.CreationDate",
      note: "Only enrollments created on or after the floor are picked up.",
    },
  },
  {
    id: "UC2",
    name: "SSO ID change",
    trigger: "scheduled",
    fires: "Every cron tick",
    jobTypes: ["UPDATE_ID"],
    scope: "All active systems",
    details:
      "Detects when uom.OpenId no longer matches the SsoId in integration_user_mapping. Enqueues UPDATE_ID jobs to rename the user in the external system. SsoId-differentiated dedup — every distinct new SSO gets its own row.",
    anchorFloor: null,
  },
  {
    id: "UC3",
    name: "Status change → deprovision",
    trigger: "inline",
    fires: "On enrollment status update via API (low-latency hook)",
    jobTypes: ["DEPROVISION"],
    scope: "All active systems",
    details:
      "When an admin/teacher changes a student's enrollment status away from Enrolled (and not to Graduated), the update-enrollment controller calls enqueueStatusChangeJobs() directly. UC7 is the scheduled backstop for status changes that didn't go through this controller.",
    anchorFloor: null,
  },
  {
    id: "UC4",
    name: "Late enrollment provision",
    trigger: "inline",
    fires: "On enrollment create/update via API (low-latency hook)",
    jobTypes: ["PROVISION"],
    scope: "All active systems",
    details:
      "When a student gets enrolled AFTER their course has already started, the controller calls enqueueLateEnrollmentJobs() so they get provisioned immediately instead of waiting for the next cron tick. UC1's window covers this same condition as a backstop.",
    anchorFloor: null,
  },
  {
    id: "UC5",
    name: "Graduation + 60 days",
    trigger: "scheduled",
    fires: "Every cron tick",
    jobTypes: ["DEPROVISION", "REVOKE_LICENSE"],
    scope: "Blackboard / SmartGantry → DEPROVISION\nM365 → REVOKE_LICENSE",
    details:
      "60 days after a student's GraduationDate this trigger fires TWO different actions: (a) BLACKBOARD / SMARTGANTRY get a full DEPROVISION (account disabled); (b) M365 gets only a REVOKE_LICENSE — the AD account stays active for the rest of the +1yr grace window so the student can still collect transcripts / alumni comms, but the paid license is reclaimed immediately. The M365 REVOKE_LICENSE branch is additionally bounded by cutoffDate so older grads aren't retro-revoked. Catch-up window — fires for any past-due student until the open-cycle dedup is closed.",
    anchorFloor: {
      column: "student_enrollment.GraduationDate (M365 branch only)",
      note: "Only the M365 REVOKE_LICENSE branch honors the floor. The Blackboard / SmartGantry DEPROVISION branch ignores it and fires for any past-due grad.",
    },
  },
  {
    id: "UC6",
    name: "Graduation + 1 year",
    trigger: "scheduled",
    fires: "Every cron tick",
    jobTypes: ["DEPROVISION"],
    scope: "M365 only",
    details:
      "1 year after a student's GraduationDate, enqueues a M365 DEPROVISION so the AD account is finally disabled (UC5 already reclaimed the license at +60d). Skipped entirely if M365 isn't in activeSystems. Catch-up window — fires for any past-due student until the open-cycle dedup is closed.",
    anchorFloor: null,
  },
  {
    id: "UC7",
    name: "Status change deprovision (cron backstop for UC3)",
    trigger: "scheduled",
    fires: "Every cron tick",
    jobTypes: ["DEPROVISION"],
    scope: "All active systems",
    details:
      "Catches enrollments whose status flipped to non-Enrolled / non-Graduated through some path that didn't go through update-enrollment.controller.ts (UC3). Looks at integration_user_mapping rows whose enrollment is no longer enrolled.",
    anchorFloor: null,
  },
];

const TriggerPointsModal: React.FC<{ show: boolean; onHide: () => void }> = ({
  show,
  onHide,
}) => (
  <Modal show={show} onHide={onHide} size="xl" centered dialogClassName="js-tp-modal">
    <Modal.Header closeButton>
      <Modal.Title>
        <ListChecks size={18} style={{ verticalAlign: "middle", marginRight: 8 }} />
        Integration trigger points (UC1 – UC7)
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="js-tp-intro">
        These are the seven trigger points that enqueue rows into
        <code> integration_job</code>. Five are driven by the
        <code> integration-enrollment-scan</code> cron; two are inline
        controller hooks that fire when the relevant API endpoint is called.
      </div>
      <table className="js-tp-table">
        <colgroup>
          <col className="js-tp-col-uc" />
          <col className="js-tp-col-trigger" />
          <col className="js-tp-col-jobtype" />
          <col className="js-tp-col-scope" />
          <col className="js-tp-col-details" />
        </colgroup>
        <thead>
          <tr>
            <th>UC</th>
            <th>Trigger</th>
            <th>Job type</th>
            <th title="Which target systems this UC applies to, plus the anchor floor (which enrollment date column cutoffDate / lookbackDays are checked against, if any).">
              Scope &amp; Anchor
            </th>
            <th>What it does</th>
          </tr>
        </thead>
        <tbody>
          {UC_BREAKDOWN.map((uc) => (
            <tr key={uc.id}>
              <td className="js-tp-id">
                <strong>{uc.id}</strong>
                <div className="js-tp-name">{uc.name}</div>
              </td>
              <td>
                <span
                  className={`js-tp-trigger js-tp-trigger-${uc.trigger}`}
                  title={uc.fires}
                >
                  {uc.trigger === "scheduled" ? "⏰ Scheduled" : "⚡ Inline"}
                </span>
                <div className="js-tp-fires">{uc.fires}</div>
              </td>
              <td>
                <div className="js-tp-jobtypes">
                  {uc.jobTypes.map((jt) => (
                    <span
                      key={jt}
                      className={`js-tp-jobtype js-tp-jt-${jt.toLowerCase()}`}
                    >
                      {jt}
                    </span>
                  ))}
                </div>
              </td>
              <td className="js-tp-scope">
                <div className="js-tp-scope-text">{uc.scope}</div>
                <div className="js-tp-scope-anchor">
                  <div className="js-tp-scope-anchor-label">Anchor floor</div>
                  {uc.anchorFloor ? (
                    <>
                      <code className="js-tp-anchor-col">
                        {uc.anchorFloor.column}
                      </code>
                      <div className="js-tp-anchor-note">
                        {uc.anchorFloor.note}
                      </div>
                    </>
                  ) : (
                    <span
                      className="js-tp-anchor-none"
                      title="This UC does not filter on cutoffDate / lookbackDays."
                    >
                      —
                    </span>
                  )}
                </div>
              </td>
              <td className="js-tp-details">{uc.details}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="js-tp-anchor-example">
        <div className="js-tp-anchor-example-title">
          Example — how the same <code>lookbackDays</code> value applies
          differently per UC
        </div>
        <p>
          Say you set <code>lookbackDays: 90</code> in this cron's config and
          today is <strong>2026-07-03</strong>. The effective anchor floor
          resolves to <strong>2026-04-04</strong> (today − 90 days). The cron
          then applies that same floor to a{" "}
          <strong>different date column depending on which UC is running</strong>:
        </p>
        <ul>
          <li>
            <strong>UC1</strong> filters{" "}
            <code>student_enrollment.CreationDate ≥ 2026-04-04</code>. Only new
            enrollments created in the last 90 days will be provisioned.
          </li>
          <li>
            <strong>UC5 (M365 branch)</strong> filters{" "}
            <code>student_enrollment.GraduationDate ≥ 2026-04-04</code>. Only
            students who graduated in the last 90 days will have their M365
            license revoked. A student who graduated on{" "}
            <code>2025-12-09</code> (206 days ago) is silently skipped.
          </li>
          <li>
            <strong>UC5 (Blackboard / SmartGantry)</strong> and every other UC
            listed as <code>—</code> above <strong>ignore the floor</strong> —
            they'll fire for any past-due student.
          </li>
        </ul>
        <p>
          To include older graduates in UC5's M365 branch, either widen the
          window (<code>lookbackDays: 365</code> gives a floor of{" "}
          <code>2025-07-03</code>, capturing the 2025-12-09 grad) or set{" "}
          <code>lookbackDays: 0</code> (no floor at all — full history is
          eligible).
        </p>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <button type="button" className="js-btn js-btn-secondary" onClick={onHide}>
        Close
      </button>
    </Modal.Footer>
  </Modal>
);

/* ── integration-enrollment-scan structured form ── */

interface EnrollmentScanConfig {
  lookbackDays?: number;
  cutoffDate?: string;
  /**
   * UC1 course-start window (days). UC1 fires provision jobs when a
   * program's courseStartDate is within this many days from today. Defaults
   * to 7 when unset. Set to 0 to only fire on the day the course begins.
   */
  courseStartWithinDays?: number;
}

const EnrollmentScanForm: React.FC<{
  initial: EnrollmentScanConfig;
  onChange: (cfg: EnrollmentScanConfig) => void;
  processorRow: JobScheduleRow | undefined;
}> = ({ initial, onChange, processorRow }) => {
  const [cfg, setCfg] = useState<EnrollmentScanConfig>(initial);
  const [showCourseStartHelp, setShowCourseStartHelp] = useState(false);

  const update = (next: EnrollmentScanConfig) => {
    setCfg(next);
    onChange(next);
  };

  const procCfg = (processorRow?.config as ProcessorConfig | null) ?? {};
  const activeSystems = procCfg.activeSystems ?? [];

  // Compute the effective lower bound the cron will use, so the admin can see
  // what their config actually resolves to without doing the MAX in their head.
  const effectiveFloor = (() => {
    const candidates: Date[] = [];
    if (typeof cfg.lookbackDays === "number" && cfg.lookbackDays > 0) {
      const d = new Date();
      d.setDate(d.getDate() - cfg.lookbackDays);
      candidates.push(d);
    }
    if (cfg.cutoffDate) {
      const d = new Date(cfg.cutoffDate);
      if (!isNaN(d.getTime())) candidates.push(d);
    }
    if (candidates.length === 0) return null;
    return new Date(Math.max(...candidates.map((d) => d.getTime())))
      .toISOString()
      .slice(0, 10);
  })();

  return (
    <div className="ip-form">
      <section className="ip-section">
        <h4 className="ip-section-title">
          <Clock size={14} /> Scan window
        </h4>
        <div className="ip-help">
          Applied to <strong>two</strong> UCs, each against a different date
          column:
          <ul className="ip-help-list">
            <li>
              <strong>UC1</strong> — new-enrollment provision scan filters{" "}
              <code>student_enrollment.CreationDate</code>.
            </li>
            <li>
              <strong>UC5 (M365 branch)</strong> — graduation +60 day
              license-revoke scan filters{" "}
              <code>student_enrollment.GraduationDate</code>.
            </li>
          </ul>
          Effective lower bound ={" "}
          <code>MAX(NOW − lookbackDays, cutoffDate)</code>. Leave both blank to
          scan full history for both UCs.
        </div>

        <div className="ip-field">
          <label>Lookback (days)</label>
          <input
            type="number"
            min={1}
            value={cfg.lookbackDays ?? ""}
            onChange={(e) =>
              update({
                ...cfg,
                lookbackDays:
                  e.target.value === "" ? undefined : Number(e.target.value),
              })
            }
            placeholder="e.g. 30"
            className="ip-input"
          />
        </div>

        <div className="ip-field">
          <label>Cutoff date</label>
          <input
            type="date"
            value={cfg.cutoffDate ?? ""}
            onChange={(e) =>
              update({
                ...cfg,
                cutoffDate: e.target.value === "" ? undefined : e.target.value,
              })
            }
            className="ip-input"
          />
        </div>

        <div className="ip-effective">
          <strong>Effective floor:</strong>{" "}
          {effectiveFloor ? (
            <code>{effectiveFloor}</code>
          ) : (
            <em>none — full history will be scanned</em>
          )}
          <OverlayTrigger
            placement="top"
            trigger={["hover", "focus", "click"]}
            rootClose
            overlay={
              <Popover id="ip-effective-help" className="ip-effective-popover">
                <Popover.Header as="h3">Which date is this compared against?</Popover.Header>
                <Popover.Body>
                  The cutoff filter checks{" "}
                  <code>student_enrollment.CreationDate</code> — i.e. the
                  moment the enrollment <em>row</em> was first inserted in
                  the database. For many tenants this is when the record was
                  created as a lead or draft, which may be days or weeks
                  before the student is actually marked Enrolled.
                  <br />
                  <br />
                  <strong>Side-effect:</strong> a student whose row was
                  created BEFORE this date will not be picked up by UC1,
                  even if their status was flipped to Enrolled TODAY.
                  <br />
                  <br />
                  <strong>Other date columns on the row</strong> (for
                  reference, not used here):
                  <ul className="ip-effective-popover-list">
                    <li>
                      <code>EnrolledStatusDate</code> — when StatusId became
                      Enrolled
                    </li>
                    <li>
                      <code>JoinDateTime</code> — when the student joined
                      the cohort
                    </li>
                    <li>
                      <code>LastModifiedDate</code> — any change to the row
                    </li>
                  </ul>
                  If you ever need a different anchor, the orchestrator's
                  UC1 query is a one-line change.
                </Popover.Body>
              </Popover>
            }
          >
            <button
              type="button"
              className="ip-effective-help-btn"
              aria-label="Explanation of which date is compared"
            >
              <Info size={12} />
            </button>
          </OverlayTrigger>
        </div>

        <div className="ip-subsection-divider" role="separator" />

        <div className="ip-field">
          <label>
            Course-start window (days)
            <button
              type="button"
              className="ip-field-hint-btn"
              aria-label={
                showCourseStartHelp
                  ? "Hide course-start window explanation"
                  : "Show course-start window explanation"
              }
              aria-expanded={showCourseStartHelp}
              onClick={() => setShowCourseStartHelp((v) => !v)}
            >
              <Info size={12} />
            </button>
          </label>
          <input
            type="number"
            min={0}
            value={cfg.courseStartWithinDays ?? ""}
            onChange={(e) =>
              update({
                ...cfg,
                courseStartWithinDays:
                  e.target.value === "" ? undefined : Number(e.target.value),
              })
            }
            placeholder="Fire for courses starting in the next N days (default: 7)"
            className="ip-input"
          />
          {showCourseStartHelp && (
            <div className="ip-field-help">
              UC1 will only enqueue a PROVISION for enrollments whose{" "}
              <code>courseStartDate</code> falls on or before{" "}
              <code>
                today +{" "}
                {typeof cfg.courseStartWithinDays === "number"
                  ? cfg.courseStartWithinDays
                  : 7}{" "}
                days
              </code>
              . Leave blank to use the default of 7 days. Set to{" "}
              <code>0</code> to only fire on the day a course begins.
            </div>
          )}
        </div>
      </section>

      <section className="ip-section ip-readonly-section">
        <h4 className="ip-section-title">
          <ShieldCheck size={14} /> Active systems
          <span className="ip-readonly-badge">read-only</span>
        </h4>
        <div className="ip-help">
          Which target systems the orchestrator enqueues for is configured on
          the <code>integration-processor</code> row, not here. Single source
          of truth.
        </div>

        {activeSystems.length > 0 ? (
          <div className="ip-system-pills">
            {activeSystems.map((s) => (
              <span key={s} className="ip-system-pill">
                {s}
              </span>
            ))}
          </div>
        ) : (
          <div className="ip-system-empty">
            {processorRow
              ? "No systems explicitly set — defaults to M365, BLACKBOARD."
              : "integration-processor row not found."}
          </div>
        )}
      </section>
    </div>
  );
};

/* ── Edit drawer ── */

const EditDrawer: React.FC<{
  row: JobScheduleRow;
  allRows: JobScheduleRow[];
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}> = ({ row, allRows, onClose, onSaved, onError }) => {
  const isProcessor = row.jobKey === "integration-processor";
  const isEnrollmentScan = row.jobKey === "integration-enrollment-scan";
  const isSmsDelivery = row.jobKey === "sms-delivery";
  const isStructured = isProcessor || isEnrollmentScan;
  const processorRow = allRows.find((r) => r.jobKey === "integration-processor");

  const [cronExpr, setCronExpr] = useState(row.cronExprUTC);
  const [enabled, setEnabled] = useState(!!row.isEnabled);
  const [configText, setConfigText] = useState(
    row.config ? JSON.stringify(row.config, null, 2) : "{\n  \n}",
  );
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedNotice, setSeedNotice] = useState<string | null>(null);
  const [structuredCfg, setStructuredCfg] = useState<ProcessorConfig>(
    (row.config as ProcessorConfig) ?? {},
  );
  const [scanCfg, setScanCfg] = useState<EnrollmentScanConfig>(
    (row.config as EnrollmentScanConfig) ?? {},
  );
  const [configError, setConfigError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showTriggerPoints, setShowTriggerPoints] = useState(false);

  const handleSeedFromGateway = async () => {
    const hasExisting =
      configText.trim() !== "" && configText.trim() !== "{}" && configText.trim() !== "{\n  \n}";
    if (hasExisting) {
      const ok = window.confirm(
        "Replace the current Config JSON with values loaded from gateway_detail?",
      );
      if (!ok) return;
    }
    setSeedLoading(true);
    setSeedNotice(null);
    try {
      const seed = await fetchSmsDeliverySeed();
      if (!seed.config) {
        setSeedNotice("No enabled SMS gateway found in gateway_detail for this tenant.");
        return;
      }
      setConfigText(JSON.stringify(seed.config, null, 2));
      setConfigError(null);
      setSeedNotice(
        seed.row
          ? `Loaded from gateway_detail#${seed.row.id} (${seed.row.name}). Review and Save.`
          : "Loaded from gateway_detail. Review and Save.",
      );
    } catch (err: any) {
      setSeedNotice(errorMessage(err, "Failed to load gateway_detail"));
    } finally {
      setSeedLoading(false);
    }
  };

  const validateAndParseConfig = (): Record<string, unknown> | null | "error" => {
    if (configText.trim() === "") return null;
    try {
      const parsed = JSON.parse(configText);
      if (parsed === null) return null;
      if (typeof parsed !== "object" || Array.isArray(parsed)) {
        setConfigError("Config must be a JSON object (or empty).");
        return "error";
      }
      setConfigError(null);
      return parsed;
    } catch (e: any) {
      setConfigError(`Invalid JSON: ${e?.message || "parse error"}`);
      return "error";
    }
  };

  const handleSave = async () => {
    let cfg: Record<string, unknown> | null;
    if (isProcessor) {
      const pruned = pruneEmpty(structuredCfg);
      cfg = pruned && Object.keys(pruned).length > 0 ? pruned : null;
    } else if (isEnrollmentScan) {
      const pruned = pruneEmpty(scanCfg);
      cfg = pruned && Object.keys(pruned).length > 0 ? pruned : null;
    } else {
      const parsed = validateAndParseConfig();
      if (parsed === "error") return;
      cfg = parsed;
    }
    setSaving(true);
    try {
      await updateJobSchedule(row.jobKey, {
        cronExprUTC: cronExpr,
        isEnabled: enabled,
        config: cfg,
      });
      await reloadJobSchedules();
      onSaved(`${row.jobName} updated and schedules reloaded.`);
    } catch (err: any) {
      onError(errorMessage(err, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="js-drawer-overlay" onClick={onClose} />
      <div className="js-drawer">
        <div className="js-drawer-header">
          <Pencil size={20} />
          <div className="js-drawer-header-text">
            <div className="js-drawer-header-title">Edit job</div>
            <div className="js-drawer-header-sub">
              {row.jobName} · {row.jobKey}
            </div>
          </div>
          <X size={20} className="js-drawer-close" onClick={onClose} />
        </div>

        <div className="js-drawer-body">
          {row.metadata?.description && (
            <div className="js-info-card">
              <Info size={16} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div>{row.metadata.description}</div>
                {row.metadata.touches && row.metadata.touches.length > 0 && (
                  <div className="js-info-card-touches">
                    Touches:{" "}
                    {row.metadata.touches.map((t) => (
                      <code key={t}>{t}</code>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {row.metadata?.warning && (
            <div className="js-warn-card">
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>{row.metadata.warning}</div>
            </div>
          )}

          {isEnrollmentScan && (
            <button
              type="button"
              className="js-tp-trigger-btn"
              onClick={() => setShowTriggerPoints(true)}
            >
              <ListChecks size={14} />
              View all trigger points (UC1 – UC7)
            </button>
          )}

          <label className="js-field-label">Schedule</label>
          <CronEditor
            value={cronExpr}
            onChange={setCronExpr}
            defaultCron={row.metadata?.defaultCron}
          />

          <div className="js-field-group">
            <label className="js-field-label">Enabled</label>
            <div className="js-enable-row">
              <ToggleSwitch
                id="drawer-enabled"
                checked={enabled}
                onChange={() => setEnabled((v) => !v)}
              />
              <span className="js-enable-text">
                {enabled
                  ? "Will fire on its scheduled cadence."
                  : "Will be skipped on every tick until re-enabled."}
              </span>
            </div>
          </div>

          {row.metadata?.configurable && (
            <div className="js-field-group">
              <label className="js-field-label">
                {isProcessor ? (
                  <>
                    <KeyRound size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                    Active systems & credentials
                  </>
                ) : isEnrollmentScan ? (
                  <>
                    <Clock size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                    Scan window
                  </>
                ) : (
                  <>
                    <Code size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                    Config (JSON)
                  </>
                )}
              </label>

              {isProcessor ? (
                <IntegrationProcessorForm
                  initial={(row.config as ProcessorConfig) ?? {}}
                  onChange={setStructuredCfg}
                />
              ) : isEnrollmentScan ? (
                <EnrollmentScanForm
                  initial={(row.config as EnrollmentScanConfig) ?? {}}
                  onChange={setScanCfg}
                  processorRow={processorRow}
                />
              ) : (
                <>
                  {isSmsDelivery && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleSeedFromGateway}
                        disabled={seedLoading}
                        title="Read the active SMS row from gateway_detail (ChannelId=2, EnableStatus=1) and pre-fill this Config JSON. Review before Save."
                        className="js-btn js-btn-secondary"
                        style={{ padding: "4px 10px", fontSize: 12 }}
                      >
                        {seedLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Zap size={12} />
                        )}
                        Load defaults from gateway_detail
                      </button>
                      {seedNotice && (
                        <span style={{ fontSize: 12, color: "var(--text-body)" }}>
                          {seedNotice}
                        </span>
                      )}
                    </div>
                  )}
                  <textarea
                    value={configText}
                    onChange={(e) => {
                      setConfigText(e.target.value);
                      setConfigError(null);
                    }}
                    rows={10}
                    placeholder={`{\n  "lookbackHours": 24,\n  "limit": 50\n}`}
                    className={`js-config-textarea${configError ? " has-error" : ""}`}
                  />
                  {configError ? (
                    <div className="js-config-error">
                      <XCircle size={12} /> {configError}
                    </div>
                  ) : row.metadata?.configSchema ? (
                    <pre className="js-config-schema">{row.metadata.configSchema}</pre>
                  ) : (
                    <div className="js-config-help">
                      Empty = use defaults. Must be a JSON object if set.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="js-drawer-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="js-btn js-btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || (!isStructured && !!configError)}
            className="js-btn js-btn-primary"
          >
            {saving ? <Spinner animation="border" size="sm" /> : <Save size={14} />}
            Save and reload
          </button>
        </div>
      </div>

      <TriggerPointsModal
        show={showTriggerPoints}
        onHide={() => setShowTriggerPoints(false)}
      />
    </>
  );
};

const AuthErrorCard: React.FC<{ message: string }> = ({ message }) => {
  const handleRelogin = () => {
    try {
      localStorage.removeItem("currentUser");
      localStorage.removeItem("token");
    } catch {
      /* ignore */
    }
    window.location.href = "/";
  };
  return (
    <div className="js-auth-card">
      <div className="js-auth-icon">
        <ShieldAlert size={20} />
      </div>
      <div style={{ flex: 1 }}>
        <div className="js-auth-title">Your session can't access this tenant</div>
        <p className="js-auth-body">
          Server replied <code>{message}</code>. Sign in again with an account
          that matches the tenant currently configured on the backend.
        </p>
        <button type="button" onClick={handleRelogin} className="js-btn-relogin">
          <ExternalLink size={14} /> Sign out and re-login
        </button>
      </div>
    </div>
  );
};

export default JobSchedulesPage;
